import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  ChevronDown, 
  ChevronUp, 
  Target, 
  Calculator, 
  Truck, 
  Users, 
  DollarSign, 
  Package, 
  Shield, 
  FileText, 
  BarChart3, 
  CreditCard, 
  TrendingUp, 
  Layers,
  Repeat,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  HelpCircle,
  X,
  ArrowRight,
  Play,
  Calendar
} from 'lucide-react';
import { useTeachingMode } from '../contexts/TeachingModeContext';
import { useNavigation } from '../contexts/NavigationContext';

const EnhancedTeachingAids = ({ module = 'salesOrder', isVisible = true }) => {
  const { toggleTeachingAids } = useTeachingMode();
  const { navigateTo } = useNavigation();
  const [expandedSections, setExpandedSections] = useState({
    processFlow: true,
    tips: false,
    commonMistakes: false,
    bestPractices: false
  });
  const [teachingAssistantData, setTeachingAssistantData] = useState({
    processFlows: [],
    teachingTips: [],
    commonMistakes: [],
    bestPractices: []
  });
  const [loading, setLoading] = useState(true);

  // Fetch teaching data from Supabase
  useEffect(() => {
    fetchTeachingData();
  }, [module]);

  const fetchTeachingData = async () => {
    try {
      setLoading(true);
      
      // Fetch teaching tips for the current module
      const { data: tipsData, error: tipsError } = await supabase
        .from('teaching_tips')
        .select('*')
        .eq('module_id', module);

      if (tipsError) throw tipsError;

      // Fetch teaching modules for process flows
      const { data: modulesData, error: modulesError } = await supabase
        .from('teaching_modules')
        .select('*');

      if (modulesError) throw modulesError;

      // Define process flows based on module
      const processFlows = getProcessFlowForModule(module);

      // Define common mistakes and best practices
      const commonMistakes = [
        {
          id: 1,
          title: '常見錯誤1',
          description: '這是一個常見的錯誤描述',
          severity: 'high',
          frequency: 'frequent',
          solution: '解決方案描述'
        }
      ];

      const bestPractices = [
        {
          id: 1,
          title: '最佳實踐1',
          description: '這是一個最佳實踐描述',
          difficulty: 'medium',
          roi_impact: 'high',
          benefits: '效益描述'
        }
      ];

      setTeachingAssistantData({
        processFlows,
        teachingTips: tipsData || [],
        commonMistakes,
        bestPractices
      });

    } catch (error) {
      console.error('Error fetching teaching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get process flow for specific module
  const getProcessFlowForModule = (moduleName) => {
    const flows = {
      salesOrder: [
        { id: 1, title: '潛客開發', description: '識別和開發潛在客戶', status: 'completed' },
        { id: 2, title: '需求分析', description: '分析客戶需求和產品匹配', status: 'completed' },
        { id: 3, title: '報價準備', description: '準備詳細的產品報價', status: 'in_progress' },
        { id: 4, title: '商務談判', description: '與客戶進行商務談判', status: 'pending' },
        { id: 5, title: '訂單確認', description: '確認銷售訂單細節', status: 'pending' },
        { id: 6, title: '履約交付', description: '執行訂單交付流程', status: 'pending' },
        { id: 7, title: '收款結算', description: '完成收款和結算流程', status: 'pending' }
      ],
      purchaseOrder: [
        { id: 1, title: '需求識別', description: '識別採購需求', status: 'completed' },
        { id: 2, title: '供應商選擇', description: '選擇合適的供應商', status: 'completed' },
        { id: 3, title: '詢價報價', description: '向供應商詢價', status: 'in_progress' },
        { id: 4, title: '評估比較', description: '評估和比較報價', status: 'pending' },
        { id: 5, title: '訂單下達', description: '下達採購訂單', status: 'pending' },
        { id: 6, title: '跟蹤監控', description: '跟蹤訂單執行情況', status: 'pending' },
        { id: 7, title: '收貨驗收', description: '收貨和驗收流程', status: 'pending' },
        { id: 8, title: '付款結算', description: '完成付款結算', status: 'pending' }
      ],
      production: [
        { id: 1, title: '生產規劃', description: '制定生產計劃', status: 'completed' },
        { id: 2, title: '物料需求', description: '計算物料需求', status: 'completed' },
        { id: 3, title: '產能規劃', description: '規劃生產產能', status: 'in_progress' },
        { id: 4, title: '訂單下達', description: '下達生產訂單', status: 'pending' },
        { id: 5, title: '生產控制', description: '控制生產過程', status: 'pending' },
        { id: 6, title: '品質檢驗', description: '進行品質檢驗', status: 'pending' },
        { id: 7, title: '成品入庫', description: '成品入庫管理', status: 'pending' }
      ],
      moneyCycle: [
        { id: 1, title: '科目設置', description: '設置會計科目', status: 'completed' },
        { id: 2, title: '分錄記錄', description: '記錄會計分錄', status: 'completed' },
        { id: 3, title: '應收管理', description: '管理應收帳款', status: 'in_progress' },
        { id: 4, title: '應付管理', description: '管理應付帳款', status: 'pending' },
        { id: 5, title: '現金流管理', description: '管理現金流', status: 'pending' },
        { id: 6, title: '財務報表', description: '生成財務報表', status: 'pending' },
        { id: 7, title: '預算管理', description: '管理預算計劃', status: 'pending' }
      ]
    };

    return flows[moduleName] || flows.salesOrder;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Navigation mapping for process flow steps
  const getNavigationTarget = (module, stepId) => {
    const navigationMap = {
      salesOrder: {
        1: { tab: 'sales-orders', subView: 'leads' },
        2: { tab: 'quotations', subView: 'create' },
        3: { tab: 'sales-orders', subView: 'create' },
        4: { tab: 'sales-orders', subView: 'credit-check' },
        5: { tab: 'sales-orders', subView: 'fulfillment' },
        6: { tab: 'sales-orders', subView: 'invoicing' },
        7: { tab: 'sales-orders', subView: 'payments' }
      },
      purchaseOrder: {
        1: { tab: 'purchase-orders', subView: 'requisitions' },
        2: { tab: 'purchase-orders', subView: 'suppliers' },
        3: { tab: 'purchase-orders', subView: 'rfq' },
        4: { tab: 'purchase-orders', subView: 'evaluation' },
        5: { tab: 'purchase-orders', subView: 'create' },
        6: { tab: 'purchase-orders', subView: 'tracking' },
        7: { tab: 'purchase-orders', subView: 'receiving' },
        8: { tab: 'purchase-orders', subView: 'verification' }
      },
      production: {
        1: { tab: 'production', subView: 'planning' },
        2: { tab: 'mrp', subView: 'requirements' },
        3: { tab: 'production', subView: 'capacity' },
        4: { tab: 'production', subView: 'orders' },
        5: { tab: 'production', subView: 'control' },
        6: { tab: 'quality-control', subView: 'inspection' },
        7: { tab: 'inventory', subView: 'finished-goods' }
      },
      moneyCycle: {
        1: { tab: 'chart-of-accounts', subView: 'setup' },
        2: { tab: 'journal', subView: 'entries' },
        3: { tab: 'financial-management', subView: 'receivables' },
        4: { tab: 'financial-management', subView: 'payables' },
        5: { tab: 'financial-management', subView: 'cash-flow' },
        6: { tab: 'financial-management', subView: 'reports' },
        7: { tab: 'financial-management', subView: 'budgets' }
      },
      qualityControl: {
        1: { tab: 'quality-control', subView: 'planning' },
        2: { tab: 'quality-control', subView: 'incoming' },
        3: { tab: 'quality-control', subView: 'in-process' },
        4: { tab: 'quality-control', subView: 'final' },
        5: { tab: 'quality-control', subView: 'nonconformance' },
        6: { tab: 'quality-control', subView: 'reports' },
        7: { tab: 'quality-control', subView: 'improvement' }
      },
      inventory: {
        1: { tab: 'inventory', subView: 'classification' },
        2: { tab: 'inventory', subView: 'forecasting' },
        3: { tab: 'inventory', subView: 'reorder' },
        4: { tab: 'inventory', subView: 'receiving' },
        5: { tab: 'inventory', subView: 'storage' },
        6: { tab: 'inventory', subView: 'picking' },
        7: { tab: 'inventory', subView: 'shipping' },
        8: { tab: 'inventory', subView: 'cycle-count' }
      }
    };

    return navigationMap[module]?.[stepId] || null;
  };

  const handleStepClick = (stepId) => {
    const target = getNavigationTarget(module, stepId);
    if (target) {
      navigateTo(target.tab, 'teaching-assistant', { 
        subView: target.subView,
        fromTeachingAssistant: true,
        processStep: stepId
      });
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      target: Target,
      calculator: Calculator,
      truck: Truck,
      users: Users,
      dollarSign: DollarSign,
      package: Package,
      shield: Shield,
      fileText: FileText,
      barChart3: BarChart3,
      creditCard: CreditCard,
      trendingUp: TrendingUp,
      layers: Layers,
      repeat: Repeat,
      calendar: Calendar,
      play: Play
    };
    return icons[iconName] || Target;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="fixed right-4 top-20 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
        <div className="text-center">Loading teaching aids...</div>
      </div>
    );
  }

  return (
    <div className="fixed right-4 top-20 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Teaching Assistant</h3>
              <p className="text-sm text-gray-600">Interactive Learning Guide</p>
            </div>
          </div>
          <button
            onClick={toggleTeachingAids}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-colors"
            title="Close Teaching Assistant Panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Process Flow Section */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('processFlow')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-gray-800">Process Flow</span>
            </div>
            {expandedSections.processFlow ? 
              <ChevronUp className="w-4 h-4 text-gray-400" /> : 
              <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>
          
          {expandedSections.processFlow && (
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {teachingAssistantData.processFlows.map((step, index) => (
                  <div key={step.id} className="flex items-center space-x-3 group">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      step.status === 'completed' ? 'bg-green-600 text-white' :
                      step.status === 'in_progress' ? 'bg-blue-600 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div 
                      className="flex-1 cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors"
                      onClick={() => handleStepClick(step.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm font-medium ${
                            step.status === 'completed' ? 'text-green-600' :
                            step.status === 'in_progress' ? 'text-blue-600' :
                            'text-gray-700'
                          }`}>
                            {step.title}
                          </p>
                          <p className="text-xs text-gray-500">{step.description}</p>
                        </div>
                        <ArrowRight className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tips & Tricks Section */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('tips')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Lightbulb className="w-4 h-4 text-yellow-600" />
              <span className="font-medium text-gray-800">Tips & Tricks</span>
            </div>
            {expandedSections.tips ? 
              <ChevronUp className="w-4 h-4 text-gray-400" /> : 
              <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>
          
          {expandedSections.tips && (
            <div className="px-4 pb-4 space-y-3">
              {teachingAssistantData.teachingTips.map((tip) => {
                const IconComponent = getIcon(tip.icon);
                return (
                  <div key={tip.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <IconComponent className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800 text-sm">{tip.title}</p>
                        <p className="text-xs text-yellow-700 mt-1">{tip.content}</p>
                        <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">
                          {tip.category}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Common Mistakes Section */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('commonMistakes')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="font-medium text-gray-800">Common Mistakes</span>
            </div>
            {expandedSections.commonMistakes ? 
              <ChevronUp className="w-4 h-4 text-gray-400" /> : 
              <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>
          
          {expandedSections.commonMistakes && (
            <div className="px-4 pb-4 space-y-3">
              {teachingAssistantData.commonMistakes.map((mistake) => (
                <div key={mistake.id} className={`border rounded-lg p-3 ${getSeverityColor(mistake.severity)}`}>
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">{mistake.title}</p>
                      <p className="text-xs mt-1 opacity-90">{mistake.description}</p>
                      <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                        <p className="font-medium">Solution:</p>
                        <p>{mistake.solution}</p>
                      </div>
                      <span className={`inline-block mt-2 px-2 py-1 text-xs rounded font-medium ${
                        mistake.severity === 'high' ? 'bg-red-100 text-red-700' :
                        mistake.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {mistake.severity.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Best Practices Section */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('bestPractices')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="font-medium text-gray-800">Best Practices</span>
            </div>
            {expandedSections.bestPractices ? 
              <ChevronUp className="w-4 h-4 text-gray-400" /> : 
              <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>
          
          {expandedSections.bestPractices && (
            <div className="px-4 pb-4 space-y-3">
              {teachingAssistantData.bestPractices.map((practice) => (
                <div key={practice.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800 text-sm">{practice.title}</p>
                      <p className="text-xs text-green-700 mt-1">{practice.description}</p>
                      <div className="mt-2 p-2 bg-green-100 rounded text-xs">
                        <p className="font-medium text-green-800">Benefit:</p>
                        <p className="text-green-700">{practice.benefit}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <HelpCircle className="w-4 h-4 text-blue-600" />
            <p className="text-sm text-blue-800 font-medium">Need Help?</p>
          </div>
          <p className="text-xs text-blue-700 mt-1">
            Click on any process step to navigate to that function. Use the navigation arrows to explore the workflow.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedTeachingAids; 